// ProgressPanel - the construction-progress mode: a date slider over the
// schedule, the tally of zone activities, the status legend, and (owner
// side) the review of which activity is about which part of the building.
//
// Nothing here is sample data. A bucket is coloured only through an
// activity a person linked to it; until then the page says what is
// missing and offers to propose the links.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Table, Tag, Tooltip, message } from "antd";
import { url, tokenRef } from "api";
import { STATUS_COLOR, STATUS_NAME, levelLabel } from "./glanceModel";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const DAY = 24 * 3600 * 1000;
const iso = (t) => new Date(t).toISOString().slice(0, 10);
const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

function describeKeys(manifest, keys) {
  const byLevelSystem = {};
  (keys || []).forEach((k) => {
    const [level, system, zone] = k.split("|");
    const id = `${level}|${system}`;
    (byLevelSystem[id] = byLevelSystem[id] || { level, system, zones: [] }).zones.push(Number(zone));
  });
  const zones = manifest.zones || [];
  return Object.values(byLevelSystem).map(({ level, system, zones: zs }) => {
    const s = (manifest.systems || []).find((x) => x.id === system);
    const where = zs.length === zones.length ? "whole level" : zs.map((z) => (zones[z] ? zones[z].label : z)).join(", ");
    return `${s ? s.label : system}, ${levelLabel(manifest, level)}: ${where}`;
  });
}

export function ProgressControls({ progress, date, onDate, tally }) {
  const span = progress && progress.span;
  if (!span) return null;
  const lo = +new Date(`${span[0]}T00:00:00Z`) - 7 * DAY;
  const hi = +new Date(`${span[1]}T00:00:00Z`) + 7 * DAY;
  const value = Math.round((+new Date(`${date}T00:00:00Z`) - lo) / DAY);
  const max = Math.round((hi - lo) / DAY);
  return (
    <div className="g-ctrl">
      <span className="g-clock">{fmt(date)}</span>
      <label htmlFor="g-pday" className="g-sr">Project date</label>
      <input id="g-pday" type="range" min="0" max={max} step="1" value={Math.max(0, Math.min(max, value))} aria-valuetext={fmt(date)}
             onChange={(e) => onDate(iso(lo + Number(e.target.value) * DAY))} />
      <button type="button" className="g-linkbtn" onClick={() => onDate(progress.today)}>Today</button>
      <div className="g-status" aria-live="polite">
        {`On ${fmt(date)}: ${tally.done} zone activities complete, ${tally.prog} in progress, ${tally.blocked} blocked, ${tally.todo} not started.`}
        {date > progress.today ? " Dates after today follow the plan." : ""}
      </div>
    </div>
  );
}

// Guide 19.5, mismatch alerts: activities whose schedule percentage and field
// percentage are 30 points or more apart, each number named for its source.
export function MismatchAlerts({ progress }) {
  const list = (progress && progress.mismatches) || [];
  if (!list.length) return null;
  return (
    <div className="g-callout" role="status">
      <b>The schedule and the field disagree on {list.length} activit{list.length === 1 ? "y" : "ies"}.</b>
      <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
        {list.slice(0, 8).map((m) => (
          <li key={m.id}>{m.name}: the schedule says {m.schedule}%, construction tracking shows {m.field}% installed{m.elements ? ` (${m.elements} tracked elements)` : ""}</li>
        ))}
      </ul>
      {list.length > 8 && <span className="g-tsub">and {list.length - 8} more</span>}
    </div>
  );
}

export function StatusLegend() {
  return (
    <div className="g-chips" aria-hidden="true">
      {Object.keys(STATUS_NAME).map((k) => <label key={k}><i style={{ background: STATUS_COLOR[k] }} />{STATUS_NAME[k]}</label>)}
      <label><i style={{ background: "var(--line)", opacity: 0.4 }} />No activity mapped</label>
    </div>
  );
}

export default function LinkReview({ projectId, manifest, progress, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [residue, setResidue] = useState(null);

  const load = useCallback(() => {
    fetch(`${url}/projects/${projectId}/glance/links`, { headers: auth() })
      .then((r) => r.json()).then((rows) => setLinks(Array.isArray(rows) ? rows : []))
      .catch(() => setLinks([]));
  }, [projectId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const post = async (path, body) => {
    const r = await fetch(`${url}/projects/${projectId}/glance/${path}`, {
      method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify(body),
    });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(out.err || `HTTP ${r.status}`);
    return out;
  };

  const propose = async (claude) => {
    setBusy(true);
    try {
      const out = await post("links/propose", { claude });
      setResidue(out);
      if (out.claude && out.claude.requested && !out.claude.started) message.warning(out.claude.why);
      else if (out.claude && out.claude.started) message.success(`Proposed ${out.proposed} from the rules; the other ${out.claude.activities} are being mapped. Refresh in a few minutes.`);
      else message.success(`Proposed ${out.proposed} links from the activity names. ${out.residueCount} could not be placed by rules.`);
      setOpen(true); load(); onChanged();
    } catch (e) { message.error(e.message); }
    setBusy(false);
  };

  const counts = (progress && progress.counts) || { accepted: 0, pending: 0, unmapped: 0, activities: 0 };
  return (
    <div className="g-empty" style={{ marginTop: 8 }}>
      <b>
        {counts.accepted
          ? `${counts.accepted} of ${counts.activities} schedule activities are mapped to the building.`
          : counts.activities ? "No schedule activity is mapped to the building yet." : "No construction schedule is loaded for this project yet."}
      </b>
      {" "}
      {counts.pending ? `${counts.pending} proposed links wait for review. ` : ""}
      {progress && progress.field && Object.keys(progress.field.zones || {}).length > 0 && (
        <>
          {`Today's colours come from the field: ${Object.keys(progress.field.zones).length} zone${Object.keys(progress.field.zones).length === 1 ? "" : "s"} reported on by ${progress.field.views.length} published technical view${progress.field.views.length === 1 ? "" : "s"}. `}
        </>
      )}
      {counts.activities
        ? "Other dates, and zones the field has not reported on, are coloured through an activity a person has linked: rules read the level, system and grids from each activity, the rest are proposed automatically, and you approve them."
        : "Import one from Schedule Import, then map its activities here."}
      {canEdit && counts.activities > 0 && (
        <div className="g-actions">
          <Button size="small" loading={busy} onClick={() => propose(false)}>Propose links from activity names</Button>
          <Tooltip title="Proposes links for the activities the rules could not place. Every answer is checked against the building data and waits for your review.">
            <Button size="small" loading={busy} onClick={() => propose(true)}>Propose, including the rest</Button>
          </Tooltip>
          <Button size="small" onClick={() => setOpen(true)}>See the links</Button>
          {counts.pending > 0 && (
            <a className="g-tsub" href={`/approval-queue?projectId=${projectId}&tab=documentation`}>{counts.pending} to decide in the Review Center</a>
          )}
        </div>
      )}
      <Modal open={open} width={1100} title="Which activity is about which part of the building" onCancel={() => setOpen(false)}
             footer={<Button onClick={() => setOpen(false)}>Close</Button>}>
        {residue && residue.residueCount > 0 && (
          <p className="g-note">
            {`${residue.residueCount} activities name no building system or level the rules can read`}
            {residue.claude && residue.claude.started ? "; links for them are being proposed." : " (for example: "}
            {!(residue.claude && residue.claude.started) && `${residue.residue.slice(0, 3).map((r) => `"${r.name}"`).join(", ")}).`}
          </p>
        )}
        <p className="g-tsub">Proposed links are decided in the <a href={`/approval-queue?projectId=${projectId}&tab=documentation`}>Review Center</a>; this list shows every link and where it stands.</p>
        <Table size="small" rowKey="id" loading={!links} dataSource={links || []} pagination={{ pageSize: 25 }}
          columns={[
            { title: "Activity", dataIndex: "activityName", width: 280 },
            { title: "Where", dataIndex: "keys", render: (keys) => describeKeys(manifest, keys).map((t) => <div key={t}>{t}</div>) },
            { title: "Proposed by", dataIndex: "by", width: 120, render: (by, r) => <><Tag color={by === "claude" ? "orange" : by === "person" ? "blue" : "default"}>{by === "deterministic" ? "rules" : by === "claude" ? "automatic" : by}</Tag>{r.confidence != null && <div className="g-tsub">{Math.round(r.confidence * 100)}% sure</div>}</> },
            { title: "Why", dataIndex: "reason", width: 220 },
            { title: "Review", dataIndex: "review", width: 150, render: (rv, r) => (
              rv === "pending" ? <Tag>Pending</Tag> : <span>{rv}{r.reviewedBy ? ` by ${r.reviewedBy}` : ""}</span>
            ) },
          ]} />
      </Modal>
    </div>
  );
}
